'use client';

import { HourglassTopRounded } from '@mui/icons-material';
import {
    Avatar,
    Badge,
    Box,
    Divider,
    IconButton,
    Menu,
    MenuItem,
    Stack,
    Typography,
} from '@mui/material';
import { useTranslations } from 'next-intl';
import { useState } from 'react';

import { ampli } from '@/ampli';
import type { OwnedDmcLink } from '@/app/api/lib/dmcs/owned-dmc-links';
import type { Role } from '@/app/api/lib/permissions/matrix';
import { shutdownIntercomMessenger } from '@/configurations/intercom/intercom-shutdown';
import { trackNavigationClick } from '@/configurations/tracking/navigation';

type UserMenuProps = {
    initials: string;
    email: string;
    displayName?: string;
    /**
     * Organisation portée par la session — l'agence pour un user agence, la DMC
     * pour un user DMC (cf. `resolveOrganizationName`). Absente pour un compte
     * en attente de validation ou quand la donnée manque côté Mirage : la
     * ligne n'est alors simplement pas rendue.
     */
    organizationName?: string;
    role: Role;
    /**
     * Fiches possédées par un DMC. Chacune porte son deep-link Odys, résolu contre l'instance
     * de SA fiche : un viewer multi-fiches obtient donc un item par instance, pas un lien unique
     * qui en désignerait forcément une à tort.
     */
    dmcLinks?: readonly OwnedDmcLink[];
    /**
     * Compte en attente de validation manuelle (`accountStatus === 'pending'`).
     * Affiche un indicateur sablier sur l'avatar + un encart explicatif en tête
     * du menu, sans pousser le contenu (remplace l'ancienne bannière pleine
     * largeur, source de shift de layout).
     */
    isPending?: boolean;
};

/**
 * Sans reset, l'`user_id` Amplitude survit à la déconnexion et les events du
 * visiteur suivant sur ce navigateur sont attribués à l'ancien compte.
 *
 * Le garde `isLoaded` n'est pas cosmétique : le getter `client` du wrapper
 * généré retourne `undefined` quand `load()` n'a pas été appelé (cas du local),
 * donc un appel non gardé lèverait un `TypeError`. Et le tracking ne doit
 * jamais empêcher une déconnexion d'aboutir — d'où le `try/catch`, qui laisse
 * `handleLogout` synchrone : le reset est local, il ne flush aucun réseau.
 */
function resetAmplitudeIdentity() {
    try {
        if (ampli.isLoaded) {
            ampli.client.reset();
        }
    } catch (error) {
        console.error('Amplitude reset failed', error);
    }
}

type OdysAppEntry = { name: string; odysAppUrl: string };

/**
 * Apps Odys atteignables par le viewer, dédupliquées par URL : plusieurs fiches servies par la
 * même instance mènent à la même app, donc à un seul item — un compte multi-fiches n'a rien à
 * arbitrer. Seules des instances réellement distinctes produisent plusieurs items.
 *
 * Une fiche sans deep-link n'a pas d'item du tout : mieux vaut aucun lien qu'un lien mort. Une
 * fiche sans nom non plus — un item de menu anonyme ne se choisit pas. Les deux vont ensemble :
 * seule une fiche non onboardée peut manquer de nom, et elle n'a jamais de deep-link.
 */
function odysAppEntries(dmcLinks: readonly OwnedDmcLink[]): OdysAppEntry[] {
    const byUrl = new Map<string, OdysAppEntry>();

    dmcLinks.forEach((link) => {
        if (link.odysAppUrl && link.name && !byUrl.has(link.odysAppUrl)) {
            byUrl.set(link.odysAppUrl, { name: link.name, odysAppUrl: link.odysAppUrl });
        }
    });

    return [...byUrl.values()];
}

export function UserMenu({
    initials,
    email,
    displayName,
    organizationName,
    role,
    dmcLinks = [],
    isPending,
}: UserMenuProps) {
    const t = useTranslations('nav');
    const tPending = useTranslations('accountPending');
    const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
    const open = Boolean(anchorEl);

    const handleOpen = (event: React.MouseEvent<HTMLElement>) => {
        trackNavigationClick({ navItem: 'user_menu', triggerContext: 'navbar' });
        setAnchorEl(event.currentTarget);
    };

    const handleClose = () => {
        setAnchorEl(null);
    };

    /**
     * On **navigue** vers la route, on ne l'appelle pas en arrière-plan : elle répond par une
     * redirection vers l'`end_session_endpoint` de Keycloak, et un `fetch` ne peut pas suivre
     * une redirection cross-origin — la session SSO survivrait au sign-out.
     *
     * Le serveur décide seul de ce qu'il termine, en lisant les cookies présents : session agence
     * du realm `catalog`, ou session DMC portée par le cookie partagé — le client n'a rien à savoir
     * de l'émetteur qui porte la session.
     */
    const handleLogout = () => {
        resetAmplitudeIdentity();
        // Pendant Intercom du reset Amplitude : sur un poste d'agence partagé, les cookies du
        // messenger survivraient à la déconnexion et l'utilisateur suivant rouvrirait le widget
        // sur la conversation du précédent.
        shutdownIntercomMessenger();
        window.location.href = '/api/auth/logout';
    };

    return (
        <>
            <IconButton
                onClick={handleOpen}
                aria-label={initials}
                aria-haspopup="menu"
                aria-expanded={open}
                sx={{ p: 0 }}
            >
                <Badge
                    overlap="circular"
                    anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
                    invisible={!isPending}
                    badgeContent={
                        <Avatar
                            sx={{
                                width: 16,
                                height: 16,
                                bgcolor: 'warning.main',
                                color: 'common.white',
                            }}
                        >
                            <HourglassTopRounded sx={{ fontSize: 12 }} />
                        </Avatar>
                    }
                >
                    <Avatar
                        sx={{
                            width: 32,
                            height: 32,
                            bgcolor: 'primary.main',
                            color: 'common.white',
                            fontWeight: 700,
                        }}
                    >
                        {initials}
                    </Avatar>
                </Badge>
            </IconButton>
            <Menu
                anchorEl={anchorEl}
                open={open}
                onClose={handleClose}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
                transformOrigin={{ vertical: 'top', horizontal: 'right' }}
                slotProps={{ paper: { sx: { minWidth: 220 } } }}
            >
                <Box sx={{ px: 2, py: 1 }}>
                    {displayName ? (
                        <Typography variant="body2" sx={{ fontWeight: 600 }}>
                            {displayName}
                        </Typography>
                    ) : null}
                    <Typography
                        variant="caption"
                        sx={{
                            color: 'text.secondary',
                            wordBreak: 'break-all',
                        }}
                    >
                        {email}
                    </Typography>
                    {organizationName ? (
                        <Typography
                            data-testid="user-menu-organization"
                            variant="caption"
                            sx={{
                                color: 'text.secondary',
                                display: 'block',
                            }}
                        >
                            {organizationName}
                        </Typography>
                    ) : null}
                </Box>
                {isPending ? (
                    <Box sx={{ px: 2, py: 1.5, maxWidth: 280 }}>
                        <Stack
                            direction="row"
                            spacing={1}
                            sx={{
                                alignItems: 'center',
                                mb: 0.5,
                            }}
                        >
                            <HourglassTopRounded sx={{ fontSize: 18, color: 'warning.main' }} />
                            <Typography
                                variant="body2"
                                sx={{ fontWeight: 600, color: 'warning.main' }}
                            >
                                {tPending('bannerTitle')}
                            </Typography>
                        </Stack>
                        <Typography
                            variant="caption"
                            sx={{
                                color: 'text.secondary',
                            }}
                        >
                            {tPending('bannerBody')}
                        </Typography>
                    </Box>
                ) : null}
                <Divider />
                {role === 'dmc'
                    ? odysAppEntries(dmcLinks).map(({ name, odysAppUrl }, index, entries) => (
                          <MenuItem
                              key={odysAppUrl}
                              divider={index === entries.length - 1}
                              component="a"
                              href={odysAppUrl}
                              target="_blank"
                              rel="noopener noreferrer"
                          >
                              {/* Le nom de la fiche n'est suffixé que s'il y a une ambiguïté à lever. */}
                              {entries.length > 1 ? `${t('openOdys')} · ${name}` : t('openOdys')}
                          </MenuItem>
                      ))
                    : null}
                {/*
                 * Libellé unique quel que soit le rôle. Côté DMC le sign-out est délégué à odys,
                 * propriétaire du cookie partagé, et termine donc AUSSI la session odys : ce n'est
                 * plus annoncé dans le libellé (cf. FD-20260810093232).
                 */}
                <MenuItem onClick={handleLogout}>{t('logout')}</MenuItem>
            </Menu>
        </>
    );
}
